import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteInfo } from '@/mocks/site';
import { Helmet } from '@/pages/home/components/Helmet';

export default function AboutPage() {
  return (
    <>
      <Helmet
        title="关于我们 - 米乐体育官方平台"
        description="米乐体育深耕体育行业多年，以专业技术和海量赛事资源服务全球体育爱好者。了解米乐体育品牌故事、发展历程和联系方式。"
        keywords="米乐体育,关于米乐体育,米乐体育联系方式,米乐体育品牌介绍"
        canonical="https://www.milesports.com/about"
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        {/* Breadcrumb */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-400">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">关于我们</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20sports%20headquarters%20office%20building%20exterior%20with%20glass%20facade%2C%20professional%20corporate%20atmosphere%2C%20warm%20sunset%20lighting%2C%20clean%20minimal%20architecture%2C%20red%20and%20white%20brand%20colors%2C%20spacious%20plaza&width=1920&height=700&seq=about-hero-01&orientation=landscape"
              alt="米乐体育"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>
          </div>
          <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4">关于米乐体育</h1>
            <p className="text-sm md:text-base text-background-50/70 max-w-2xl mx-auto">
              {siteInfo.slogan}，汇聚全球顶级联赛资源，为体育爱好者带来极致观赛体验与互动乐趣
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="text-xs font-bold text-primary-500 uppercase tracking-wider">Brand Story</span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2 mb-6">品牌故事</h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>米乐体育成立于2018年，总部位于上海，是一家专注于体育赛事直播与数据服务的综合平台。自成立以来，我们始终坚持"让每一场赛事更有价值"的理念，致力于为全球体育爱好者提供极致的观赛体验。</p>
                  <p>经过多年的发展，米乐体育已经与全球50多个顶级体育联赛建立了深度合作关系，涵盖了足球、篮球、网球、电竞等热门体育项目。我们的平台日均直播赛事超过2000场，累计注册用户突破500万。</p>
                  <p>米乐体育拥有超过200人的专业技术团队，在视频直播技术、数据分析和人工智能领域持续投入，确保为用户提供高清流畅的直播体验和专业精准的数据服务。</p>
                </div>
              </div>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20open%20plan%20office%20interior%20with%20employees%20working%20at%20desks%2C%20large%20LED%20screens%20displaying%20sports%20data%2C%20warm%20modern%20design%2C%20collaborative%20workspace%2C%20red%20accent%20walls%2C%20natural%20light%20from%20large%20windows&width=800&height=600&seq=about-story-01&orientation=landscape"
                  alt="米乐体育办公环境"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 bg-background-100/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: 'ri-calendar-check-line', value: '2018年', label: '成立时间' },
                { icon: 'ri-user-star-line', value: '500万+', label: '注册用户' },
                { icon: 'ri-global-line', value: '50+', label: '覆盖国家/地区' },
                { icon: 'ri-team-line', value: '200+', label: '专业团队' },
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <div className="w-14 h-14 rounded-2xl bg-accent-50 mx-auto flex items-center justify-center mb-3">
                    <i className={`${item.icon} text-accent-500 text-2xl`}></i>
                  </div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{item.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="text-xs font-bold text-primary-500 uppercase tracking-wider">Development</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">发展历程</h2>
            </div>
            <div className="max-w-3xl mx-auto">
              {[
                { year: '2018', title: '扬帆起航', desc: '米乐体育在上海正式成立，推出赛事直播平台1.0版本' },
                { year: '2019', title: '快速成长', desc: '用户规模突破50万，与10个顶级联赛建立合作关系' },
                { year: '2020', title: '技术升级', desc: '上线4K超高清直播技术，推出AI数据智能分析系统' },
                { year: '2022', title: '全球化布局', desc: '业务覆盖50+国家地区，上线多语言版本平台' },
                { year: '2024', title: '行业领先', desc: '用户突破500万，日均赛事直播超2000场' },
                { year: '2026', title: '持续创新', desc: '推出全新竞猜互动玩法，打造全方位体育服务生态' },
              ].map((item, idx) => (
                <div key={item.year} className="flex gap-6">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                      <span className="font-heading text-xs font-bold text-background-50">{item.year}</span>
                    </div>
                    {idx < 5 && <div className="w-0.5 flex-1 bg-background-200 my-1"></div>}
                  </div>
                  <div className="pb-10">
                    <h4 className="font-bold text-foreground-900 text-base">{item.title}</h4>
                    <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-16 md:py-20 bg-background-100/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="text-xs font-bold text-primary-500 uppercase tracking-wider">Contact Us</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">联系我们</h2>
              <p className="text-sm text-foreground-500 mt-2">有任何问题或合作意向，欢迎随时联系我们</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
              <div className="space-y-5">
                {[
                  { icon: 'ri-phone-line', label: '客服热线', value: siteInfo.phone },
                  { icon: 'ri-mail-line', label: '电子邮箱', value: siteInfo.email },
                  { icon: 'ri-map-pin-line', label: '公司地址', value: siteInfo.address },
                  { icon: 'ri-time-line', label: '工作时间', value: '周一至周日 9:00 - 22:00' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-accent-500 text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">{item.label}</p>
                      <p className="text-sm font-bold text-foreground-900">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <h3 className="font-bold text-foreground-900 mb-5">发送消息</h3>
                <form
                  id="d9sllet8o1vn02ekuhgg"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d9sllet8o1vn02ekuhgg"
                  method="POST"
                  encType="application/x-www-form-urlencoded"
                  className="space-y-4"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget as HTMLFormElement;
                    const honeypot = form.querySelector('[name="company_alt"]') as HTMLInputElement;
                    if (honeypot && honeypot.value.trim() !== '') {
                      const msgEl = document.getElementById('about-form-msg');
                      if (msgEl) { msgEl.textContent = '消息已发送，我们会尽快回复您！'; msgEl.className = 'text-xs text-accent-500 mt-2'; }
                      form.reset();
                      return;
                    }
                    try {
                      const formData = new FormData(form);
                      const body = new URLSearchParams();
                      formData.forEach((value, key) => {
                        if (key !== 'company_alt' || (value as string).trim() === '') {
                          body.append(key, value as string);
                        }
                      });
                      const res = await fetch(form.action, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
                      const responseText = await res.text();
                      const msgEl = document.getElementById('about-form-msg');
                      let parsed;
                      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
                      if (res.ok && parsed?.code === 'OK') {
                        if (msgEl) { msgEl.textContent = '感谢您的留言，我们会尽快回复您！'; msgEl.className = 'text-xs text-accent-500 mt-2'; }
                        form.reset();
                      } else {
                        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后重试';
                        if (msgEl) { msgEl.textContent = serverMsg; msgEl.className = 'text-xs text-primary-500 mt-2'; }
                      }
                    } catch {
                      const msgEl = document.getElementById('about-form-msg');
                      if (msgEl) { msgEl.textContent = '网络错误，请稍后重试'; msgEl.className = 'text-xs text-primary-500 mt-2'; }
                    }
                  }}
                >
                  <div>
                    <label htmlFor="about-name" className="block text-xs font-medium text-foreground-600 mb-1.5">姓名</label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-xs font-medium text-foreground-600 mb-1.5">邮箱</label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-xs font-medium text-foreground-600 mb-1.5">留言</label>
                    <textarea
                      id="about-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    className="w-full py-3 rounded-lg text-sm font-bold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                  <p id="about-form-msg" className="text-xs mt-2"></p>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="text-xs font-bold text-primary-500 uppercase tracking-wider">FAQ</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">常见问题</h2>
            </div>
            <div className="space-y-3">
              {[
                { q: '米乐体育平台提供哪些体育赛事直播？', a: '米乐体育平台提供足球、篮球、网球、电竞等全球热门赛事的高清直播服务，覆盖英超、西甲、意甲、德甲、法甲、NBA、CBA、ATP/WTA巡回赛、LCK、LPL等顶级联赛与赛事。' },
                { q: '如何在米乐体育注册账号？', a: '您可以通过米乐体育官网或米乐体育APP进行注册，使用手机号码快速完成验证即可。新用户注册还可享受专属福利，包括免费观看部分付费赛事和竞猜体验金。' },
                { q: '米乐体育的竞猜功能如何使用？', a: '在米乐体育平台选择您感兴趣的赛事，查看实时赔率数据，选择竞猜选项后提交即可参与。平台提供专业的AI数据分析和历史对比，帮助您做出更准确的判断。' },
                { q: '米乐体育APP在哪里下载？', a: '您可以在米乐体育官网首页点击"下载APP"按钮获取安装包，支持iOS和Android系统。也可以在苹果App Store和各大安卓应用商店搜索"米乐体育"进行下载。' },
                { q: '观看直播需要付费吗？', a: '米乐体育提供大量免费赛事直播内容，部分顶级赛事和高清直播频道可能需要VIP会员或单场付费。新用户注册即可获得7天VIP体验权益。' },
              ].map((faq, idx) => (
                <details key={idx} className="group bg-background-100/70 rounded-xl border border-background-200/50">
                  <summary className="flex items-center justify-between p-5 cursor-pointer text-sm font-bold text-foreground-900 group-open:text-primary-500">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-lg transition-transform duration-200 group-open:rotate-180"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}